import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListDailyGoalsQuery,
  useListAssigneesQuery,
  useUpsertDailyGoalMutation,
  useDeleteDailyGoalMutation,
} from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { DailyGoal } from '../types/models';

interface FormData {
  assignee: string;
  goal: string;
  date: string;
}

export default function DailyGoalsPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);
  const dispatch = useAppDispatch();

  const { data: goals = [], isLoading } = useListDailyGoalsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const { data: assignees = [] } = useListAssigneesQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );
  const [upsert] = useUpsertDailyGoalMutation();
  const [remove] = useDeleteDailyGoalMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DailyGoal | null>(null);
  const [toDelete, setToDelete] = useState<DailyGoal | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } =
    useForm<FormData>();

  if (!projectId || !sprintId) {
    return (
      <>
        <PageHeader title="Daily Goals" />
        <Alert severity="info">Select a project and sprint.</Alert>
      </>
    );
  }

  const openCreate = () => {
    setEditing(null);
    reset({ assignee: '', goal: '', date: format(new Date(), 'yyyy-MM-dd') });
    setOpen(true);
  };

  const openEdit = (g: DailyGoal) => {
    setEditing(g);
    reset({ assignee: g.assignee, goal: g.goal, date: g.date });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await upsert({
        uid,
        projectId,
        sprintId,
        id: editing?.id,
        ...data,
      }).unwrap();
      dispatch(showToast({ message: editing ? 'Goal updated' : 'Goal added', severity: 'success' }));
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, sprintId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Goal deleted', severity: 'success' }));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  // Group by date desc
  const grouped = goals.reduce<Record<string, DailyGoal[]>>((acc, g) => {
    (acc[g.date] ||= []).push(g);
    return acc;
  }, {});
  const dateKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <>
      <PageHeader
        title="Daily Goals"
        subtitle="Per-assignee daily commitments inside the active sprint."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add goal
          </Button>
        }
      />

      {!isLoading && goals.length === 0 ? (
        <EmptyState
          title="No daily goals"
          description="Add a daily goal for an assignee."
          action={<Button variant="contained" onClick={openCreate}>Add goal</Button>}
        />
      ) : (
        <Stack spacing={2}>
          {dateKeys.map((date) => (
            <Paper key={date} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Chip label={date} color="primary" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {grouped[date].length} goal(s)
                </Typography>
              </Stack>
              <List dense disablePadding>
                {grouped[date].map((g) => (
                  <ListItem
                    key={g.id}
                    secondaryAction={
                      <>
                        <IconButton size="small" onClick={() => openEdit(g)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setToDelete(g)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={g.assignee} />
                          <Typography>{g.goal}</Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit goal' : 'New daily goal'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                select
                label="Assignee"
                error={!!errors.assignee}
                helperText={errors.assignee?.message}
                {...register('assignee', { required: 'Required' })}
                defaultValue=""
              >
                <MenuItem value="">— select —</MenuItem>
                {assignees.map((a) => (
                  <MenuItem key={a.id} value={a.name}>
                    {a.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Goal"
                multiline
                rows={2}
                error={!!errors.goal}
                helperText={errors.goal?.message}
                {...register('goal', { required: 'Required' })}
              />
              <TextField
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...register('date', { required: 'Required' })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete goal?"
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
